// Customizable Area Start
import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Grid,
  Button,
  Chip,
  styled,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import NavigationIcon from "@material-ui/icons/Navigation";
import CloseIcon from "@material-ui/icons/Close";
import AddShoppingCartIcon from "@material-ui/icons/AddShoppingCart";
import { Alert } from "@material-ui/lab";
import CustomLoader from "../../../components/src/customComponents/CustomLoader.web";

import MobileRequestsController, {
  Props,
  STATUS_OPTIONS,
  IMobileServiceRequest,
} from "./MobileRequestsController.web";
// Customizable Area End

export default class MobileRequests extends MobileRequestsController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start

  getStatusColor = (status: string) => {
    const colorMap: { [key: string]: { bg: string; color: string } } = {
      pending: { bg: "#FEF3C7", color: "#92400E" },
      accepted: { bg: "#D1FAE5", color: "#065F46" },
      pickup_in_progress: { bg: "#DBEAFE", color: "#1E40AF" },
      picked_up: { bg: "#E0E7FF", color: "#3730A3" },
      order_created: { bg: "#EDE9FE", color: "#5B21B6" },
      ready: { bg: "#D1FAE5", color: "#065F46" },
      out_for_delivery: { bg: "#FEF3C7", color: "#92400E" },
      delivered: { bg: "#D1FAE5", color: "#065F46" },
      cancelled: { bg: "#FEE2E2", color: "#991B1B" },
    };
    return colorMap[status] || { bg: "#F3F4F6", color: "#374151" };
  };

  renderDetailDialog() {
    const { detailDialogOpen, detailData, detailLoading } = this.state;
    const attrs = detailData?.attributes;

    return (
      <Dialog
        open={detailDialogOpen}
        onClose={this.handleCloseDetail}
        maxWidth="md"
        fullWidth
        data-test-id="detailDialog"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={webStyle.dialogTitle}>
              Request Details {attrs ? `- ${attrs.request_number}` : ""}
            </Typography>
            <IconButton onClick={this.handleCloseDetail} data-test-id="closeDetailBtn">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" padding={4}>
              <CustomLoader loading={true} />
            </Box>
          ) : attrs ? (
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <DetailLabel>Request Number</DetailLabel>
                <DetailValue>{attrs.request_number}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Status</DetailLabel>
                <Chip
                  label={this.formatStatus(attrs.status)}
                  style={{
                    backgroundColor: this.getStatusColor(attrs.status).bg,
                    color: this.getStatusColor(attrs.status).color,
                    fontWeight: 600,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Customer</DetailLabel>
                <DetailValue>{attrs.customer?.full_name}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Phone</DetailLabel>
                <DetailValue>{attrs.customer?.phone_number}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Service</DetailLabel>
                <DetailValue>{attrs.mobile_service?.name}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Store</DetailLabel>
                <DetailValue>{attrs.store?.name || "--"}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Pickup Type</DetailLabel>
                <DetailValue>{this.formatStatus(attrs.pickup_type)}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Pickup Scheduled At</DetailLabel>
                <DetailValue>{this.formatDate(attrs.pickup_scheduled_at)}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Delivery Type</DetailLabel>
                <DetailValue>{this.formatStatus(attrs.delivery_type)}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Delivery Scheduled At</DetailLabel>
                <DetailValue>
                  {this.formatDate(attrs.delivery_scheduled_at)}
                </DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Created At</DetailLabel>
                <DetailValue>{this.formatDate(attrs.created_at)}</DetailValue>
              </Grid>
              <Grid item xs={6}>
                <DetailLabel>Order ID</DetailLabel>
                <DetailValue>{attrs.order_id || "--"}</DetailValue>
              </Grid>
              <Grid item xs={12}>
                <DetailLabel>Notes</DetailLabel>
                <DetailValue>{attrs.notes || "--"}</DetailValue>
              </Grid>
              {attrs.cancellation_reason && (
                <Grid item xs={12}>
                  <DetailLabel>Cancellation Reason</DetailLabel>
                  <DetailValue>{attrs.cancellation_reason}</DetailValue>
                </Grid>
              )}
              <Grid item xs={6}>
                <DetailLabel>Assigned Driver</DetailLabel>
                <DetailValue>{attrs.assigned_driver?.name || "--"}</DetailValue>
              </Grid>

              {attrs.pickup_address && (
                <Grid item xs={12}>
                  <DetailLabel>Pickup Address</DetailLabel>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <DetailValue>
                      {attrs.pickup_address.label} - {attrs.pickup_address.address}
                    </DetailValue>
                    <Button
                      data-test-id="pickupMapBtn"
                      variant="outlined"
                      size="small"
                      startIcon={<NavigationIcon />}
                      onClick={() =>
                        this.openMapNavigation(
                          attrs.pickup_address!.latitude,
                          attrs.pickup_address!.longitude
                        )
                      }
                      style={webStyle.mapButton}
                    >
                      Map
                    </Button>
                  </Box>
                </Grid>
              )}

              {attrs.delivery_address && (
                <Grid item xs={12}>
                  <DetailLabel>Delivery Address</DetailLabel>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <DetailValue>
                      {attrs.delivery_address.label} -{" "}
                      {attrs.delivery_address.address}
                    </DetailValue>
                    <Button
                      data-test-id="deliveryMapBtn"
                      variant="outlined"
                      size="small"
                      startIcon={<NavigationIcon />}
                      onClick={() =>
                        this.openMapNavigation(
                          attrs.delivery_address!.latitude,
                          attrs.delivery_address!.longitude
                        )
                      }
                      style={webStyle.mapButton}
                    >
                      Map
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Customizable Area End

  render() {
    // Customizable Area Start
    const {
      requestList,
      page,
      pageSize,
      totalCount,
      isLoading,
      statusMenuAnchor,
      snackbarOpen,
      snackbarMessage,
      snackbarSeverity,
    } = this.state;

    return (
      <Box style={webStyle.mainWrapper}>
        <CustomLoader loading={isLoading} />
        <Box style={webStyle.headerRow}>
          <Typography style={webStyle.pageTitle}>Mobile Requests</Typography>
        </Box>

        <TableContainer component={Paper} style={webStyle.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledHeadCell>Request Number</StyledHeadCell>
                <StyledHeadCell>Pickup Type</StyledHeadCell>
                <StyledHeadCell>Created At</StyledHeadCell>
                <StyledHeadCell>Pickup Scheduled At</StyledHeadCell>
                <StyledHeadCell>Status</StyledHeadCell>
                <StyledHeadCell align="center">Actions</StyledHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requestList.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography style={webStyle.noDataText}>
                      No requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requestList.map((row: IMobileServiceRequest) => {
                  const statusColor = this.getStatusColor(row.attributes.status);
                  return (
                    <TableRow key={row.id} hover data-test-id={`request-row-${row.id}`}>
                      <StyledBodyCell>
                        {row.attributes.request_number}
                      </StyledBodyCell>
                      <StyledBodyCell>
                        {this.formatStatus(row.attributes.pickup_type)}
                      </StyledBodyCell>
                      <StyledBodyCell>
                        {this.formatDate(row.attributes.created_at)}
                      </StyledBodyCell>
                      <StyledBodyCell>
                        {this.formatDate(row.attributes.pickup_scheduled_at)}
                      </StyledBodyCell>
                      <StyledBodyCell>
                        <Chip
                          label={this.formatStatus(row.attributes.status)}
                          size="small"
                          style={{
                            backgroundColor: statusColor.bg,
                            color: statusColor.color,
                            fontWeight: 600,
                            fontFamily: "Montserrat",
                          }}
                        />
                      </StyledBodyCell>
                      <StyledBodyCell align="center">
                        <IconButton
                          data-test-id={`editStatus-${row.id}`}
                          size="small"
                          onClick={(e) =>
                            this.handleStatusMenuOpen(e, row.id)
                          }
                        >
                          <EditIcon style={{ color: "#204B9C", fontSize: 20 }} />
                        </IconButton>
                        <IconButton
                          data-test-id={`viewDetail-${row.id}`}
                          size="small"
                          onClick={() => this.getRequestDetail(row.id)}
                        >
                          <VisibilityIcon
                            style={{ color: "#204B9C", fontSize: 20 }}
                          />
                        </IconButton>
                        <IconButton
                          data-test-id={`createOrder-${row.id}`}
                          size="small"
                          title="Create Order"
                          onClick={() => this.handleCreateOrder(row)}
                        >
                          <AddShoppingCartIcon
                            style={{ color: "#204B9C", fontSize: 20 }}
                          />
                        </IconButton>
                      </StyledBodyCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page - 1}
            onPageChange={this.handlePageChange}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10]}
            data-test-id="pagination"
          />
        </TableContainer>

        <Menu
          anchorEl={statusMenuAnchor}
          open={Boolean(statusMenuAnchor)}
          onClose={this.handleStatusMenuClose}
          data-test-id="statusMenu"
          PaperProps={{ style: { maxHeight: 300 } }}
        >
          {STATUS_OPTIONS.map((status) => (
            <MenuItem
              key={status}
              data-test-id={`status-option-${status}`}
              onClick={() => this.handleStatusSelect(status)}
              style={webStyle.menuItem}
            >
              {this.formatStatus(status)}
            </MenuItem>
          ))}
        </Menu>

        {this.renderDetailDialog()}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={this.handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          data-test-id="snackbar"
        >
          <Alert onClose={this.handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const StyledHeadCell = styled(TableCell)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: 14,
  color: "#1A1A1A",
  backgroundColor: "#F8FAFC",
  borderBottom: "2px solid #E2E8F0",
  padding: "14px 16px",
  whiteSpace: "nowrap",
});

const StyledBodyCell = styled(TableCell)({
  fontFamily: "Montserrat",
  fontSize: 14,
  color: "#1A1A1A",
  padding: "12px 16px",
  borderBottom: "1px solid #E2E8F0",
});

const DetailLabel = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: 13,
  color: "#64748B",
  marginBottom: 4,
});

const DetailValue = styled(Typography)({
  fontFamily: "Montserrat",
  fontSize: 15,
  color: "#1A1A1A",
});

const webStyle: { [key: string]: React.CSSProperties } = {
  mainWrapper: {
    fontFamily: "Montserrat",
    padding: "24px 48px",
    background: "#FFFFFF",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  pageTitle: {
    fontFamily: "Montserrat",
    fontSize: 24,
    fontWeight: 600,
    color: "#1A1A1A",
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow:
      "0px 4px 8px rgba(0, 0, 0, 0.03), 0px 8px 32px rgba(0, 0, 0, 0.06)",
  },
  noDataText: {
    fontFamily: "Montserrat",
    fontSize: 16,
    color: "#64748B",
    padding: 32,
  },
  menuItem: {
    fontFamily: "Montserrat",
    fontSize: 14,
  },
  dialogTitle: {
    fontFamily: "Montserrat",
    fontSize: 20,
    fontWeight: 600,
  },
  mapButton: {
    borderColor: "#204B9C",
    color: "#204B9C",
    fontFamily: "Montserrat",
    fontWeight: 600,
    textTransform: "none" as const,
    minWidth: 80,
  },
};
// Customizable Area End
